if (!customElements.get('casa-verde-preregistration')) {
  customElements.define('casa-verde-preregistration', class CasaVerdePreregistration extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.messages = JSON.parse(this.querySelector('[data-cvb-messages]').textContent);
      this.form = this.querySelector('form');
      this.pvInput = this.querySelector('[data-score-pv]');
      this.storageInput = this.querySelector('[data-score-storage]');
      this.contributionInput = this.querySelector('[data-score-contribution]');
      this.existingSelect = this.querySelector('[data-existing-batteries]');
      this.existingFields = this.querySelector('[data-existing-fields]');
      this.existingWarning = this.querySelector('[data-existing-warning]');
      this.scoreField = this.querySelector('[data-score-field]');
      this.errorSummary = this.querySelector('[data-error-summary]');

      [this.pvInput, this.storageInput, this.contributionInput].forEach((input) => {
        input?.addEventListener('input', () => {
          if (input === this.storageInput) this.validateStorageInput();
          this.updateScore();
        });
      });

      this.existingSelect?.addEventListener('change', () => this.updateExistingBatteryFields());
      this.form?.addEventListener('invalid', () => window.setTimeout(() => this.showErrors(), 0), true);
      this.form?.addEventListener('submit', (event) => this.handleSubmit(event));

      this.updateExistingBatteryFields();
      this.validateStorageInput();
      this.updateScore();
    }

    decimalValue(rawValue) {
      const normalizedValue = String(rawValue ?? '').trim().replace(',', '.');
      if (!/^\d+(?:\.\d{1,2})?$/.test(normalizedValue)) return Number.NaN;

      const value = Number(normalizedValue);
      return Number.isFinite(value) ? value : Number.NaN;
    }

    numberValue(input) {
      const value = this.decimalValue(input?.value || '0');
      return Number.isFinite(value) ? value : 0;
    }

    validateStorageInput() {
      if (!this.storageInput) return true;

      const rawValue = this.storageInput.value.trim();
      const value = this.decimalValue(rawValue);
      const min = Number(this.storageInput.dataset.decimalMin || 12);
      const max = Number(this.storageInput.dataset.decimalMax || 1000);
      let message = '';

      if (!rawValue) {
        message = this.messages.storage_required;
      } else if (!Number.isFinite(value)) {
        message = this.messages.storage_decimal;
      } else if (value < min || value > max) {
        message = this.messages.storage_range.replace('{{ min }}', String(min)).replace('{{ max }}', String(max));
      }

      this.storageInput.setCustomValidity(message);
      return message === '';
    }

    handleSubmit(event) {
      const storageIsValid = this.validateStorageInput();
      this.updateScore();

      if (storageIsValid) return;

      event.preventDefault();
      this.showErrors();
      this.storageInput?.focus();
    }

    pointsLabel(value) {
      return value.toLocaleString('ro-RO', { maximumFractionDigits: 1, minimumFractionDigits: 0 });
    }

    updateScore() {
      const pvKw = this.numberValue(this.pvInput);
      const storageKwh = this.numberValue(this.storageInput);
      const contributionPercent = this.numberValue(this.contributionInput);
      const contributionPoints = Math.min(40, Math.max(0, (80 * contributionPercent / 100) - 10));
      const storagePoints = Math.min(40, Math.max(0, storageKwh));
      const pvPoints = Math.min(20, Math.max(0, pvKw));
      const total = contributionPoints + storagePoints + pvPoints;

      const scoreOutput = this.querySelector('[data-score-total]');
      const contributionOutput = this.querySelector('[data-score-contribution-output]');
      const storageOutput = this.querySelector('[data-score-storage-output]');
      const pvOutput = this.querySelector('[data-score-pv-output]');

      if (scoreOutput) scoreOutput.textContent = this.pointsLabel(total);
      if (contributionOutput) contributionOutput.textContent = `${this.pointsLabel(contributionPoints)} / 40`;
      if (storageOutput) storageOutput.textContent = `${this.pointsLabel(storagePoints)} / 40`;
      if (pvOutput) pvOutput.textContent = `${this.pointsLabel(pvPoints)} / 20`;
      if (this.scoreField) {
        this.scoreField.value = this.messages.score_estimate.replace('{{ score }}', this.pointsLabel(total));
      }
    }

    updateExistingBatteryFields() {
      const hasExisting = this.existingSelect?.value === 'Da';
      if (!this.existingFields) return;

      this.existingFields.hidden = !hasExisting;
      this.existingWarning.hidden = !hasExisting;
      this.existingSelect?.setAttribute('aria-expanded', hasExisting ? 'true' : 'false');

      this.existingFields.querySelectorAll('input').forEach((input) => {
        input.disabled = !hasExisting;
        input.required = hasExisting;
      });
    }

    showErrors() {
      if (!this.form || !this.errorSummary) return;
      const invalidFields = Array.from(this.form.querySelectorAll(':invalid'));
      if (!invalidFields.length) {
        this.errorSummary.hidden = true;
        return;
      }

      const labels = invalidFields.map((field) => {
        const label = Array.from(this.form.querySelectorAll('label')).find((candidate) => candidate.htmlFor === field.id);
        return label?.textContent.replace('*', '').trim() || this.messages.required_field;
      });
      const uniqueLabels = [...new Set(labels)];
      this.errorSummary.innerHTML = `<p><strong>${this.messages.error_heading}</strong></p><ul>${uniqueLabels.map((label) => `<li>${label}</li>`).join('')}</ul>`;
      this.errorSummary.hidden = false;
      this.errorSummary.focus();
    }
  });
}
